import type { ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { ColorNeutralGrey3, Spacing8, Spacing12, Spacing40 } from 'autogen/design-tokens/tokens';
import { CarouselScrollButton, EmblaCarousel, type ScrollType, Viewport } from 'components/emblaCarousel';
import imageOverlayStyle from 'components/shared/style/imageOverlay';
import { useIsChanel } from 'hooks/user/useIsBrand';
import type { onCarouselScrollClick } from '../shared/types';

// #region Styles
const heightOfThumbnail = 120;

const Grid = styled.div<{ $isChanel: boolean }>`
    display: grid;
    height: 100%;
    width: 100%;
    grid-auto-flow: row;
    grid-auto-rows: ${heightOfThumbnail}px;
    row-gap: ${Spacing12};

    ${({ $isChanel }) =>
        $isChanel &&
        css`
            grid-auto-rows: 80px;
            row-gap: ${Spacing8};
        `}
`;

const StyledViewport = styled(Viewport)<{ $itemsVisibleInViewport: number; $isChanel: boolean }>`
    height: 100%;
    margin-top: ${Spacing40};
    margin-bottom: ${Spacing40};

    ${({ $isChanel }) =>
        $isChanel &&
        css`
            margin-top: unset;
            margin-bottom: unset;
        `}
`;

const StyledCarouselScrollButton = styled(CarouselScrollButton)<{ scrollType: ScrollType }>`
    position: absolute;
    ${({ scrollType }) =>
        scrollType === 'next'
            ? css`
                  bottom: 0;
              `
            : css`
                  top: 0;
              `}

    left: 50%;
    transform: translateX(-50%);
    border-radius: 0;
    z-index: 1;

    &:disabled {
        pointer-events: none;
        opacity: 0;
    }

    && {
        box-shadow: none;
    }
`;
// #endregion Styles

export function GalleryThumbnailCarousel({
    itemsVisibleInViewport = 4,
    children,
    onCarouselScrollClick
}: {
    itemsVisibleInViewport?: number;
    children: ReactNode;
    onCarouselScrollClick?: onCarouselScrollClick;
}) {
    const isChanel = useIsChanel();

    return (
        <EmblaCarousel
            options={{ axis: 'y', align: 'start', containScroll: 'trimSnaps', slidesToScroll: 'auto', duration: 20 }}
        >
            <StyledCarouselScrollButton
                onClickCallBack={onCarouselScrollClick}
                direction="vertical"
                scrollType="previous"
                size="sm"
            />
            <StyledViewport $itemsVisibleInViewport={itemsVisibleInViewport} $isChanel={isChanel}>
                <Grid $isChanel={isChanel}>{children}</Grid>
            </StyledViewport>
            <StyledCarouselScrollButton
                onClickCallBack={onCarouselScrollClick}
                direction="vertical"
                scrollType="next"
                size="sm"
            />
        </EmblaCarousel>
    );
}

export const MediaCarouselItem = styled.button<{ $isChanel?: boolean; $isActive: boolean }>`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0;
    border: 1px solid ${ColorNeutralGrey3};
    background: transparent;

    &:hover {
        outline: 2px solid black;
        outline-offset: -2px;
        cursor: pointer;
    }

    &:after {
        content: '';
        ${imageOverlayStyle}
    }

    ${({ $isActive }) =>
        $isActive &&
        css`
            outline: 1px solid black;
            outline-offset: -1px;
        `}

    ${({ $isChanel }) =>
        $isChanel &&
        css`
            border: unset;

            &:after {
                content: none;
            }
        `}
`;

export default Object.assign(GalleryThumbnailCarousel, { MediaItem: MediaCarouselItem });
